import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { TextButton } from 'components/v2/buttons';
import FontIcon from 'components/icons/FontIcon';
import SelectorButtonSkeleton from 'components/v2/buttons/SelectorButtonSkeleton';
import DialogClearStyles, { DialogContent } from 'components/accounts/DialogClearStyles';
import { HeaderDialog } from 'styles/Styles.styled';
import { HelpBox } from 'components/v2/help-boxes';
import ToolVersionStatsList from './ToolVersionStatsList';
import { ToolVersionStatsType } from 'types/tools/stats/ToolVersionStatsType';
import { ToolType } from 'types/tools/ToolType';
import { useToolVersionsStats } from 'services/api/toolsQueries';
import OldUIReadonlyTextInput from './OldUIReadonlyTextInput';

interface Props {
  variant: 'admin' | 'toolOwner';
  toolData: {
    id: string;
    toolType: ToolType;
    hasApiIntegration: boolean | null;
  };
  versionData: {
    ord: number;
  };
}

const ToolVersionStats: React.FC<Props> = ({ variant, toolData, versionData }) => {
  const allStats = useToolVersionsStats(toolData.id).data;
  const versionStats = allStats?.find((v) => v.versionOrd === versionData.ord);

  if (!allStats || !versionStats) {
    return (
      <>
        <SelectorButtonSkeleton />
        <SelectorButtonSkeleton />
      </>
    );
  }

  return (
    <>
      <StatsItem
        variant={variant}
        toolId={toolData.id}
        versionOrd={versionData.ord}
        label="Number of created EPDs from Tool Version"
        buttonLabel="All linked EPDs"
        statsType={ToolVersionStatsType.LINKED_EPDS}
        value={versionStats.linkedEpdsCount}
        dialogHeaderText="EPDs created from the tool version"
        dialogHelpText="Here you can find the list of all created EPDs with this tool version."
      />
      <StatsItem
        variant={variant}
        toolId={toolData.id}
        versionOrd={versionData.ord}
        label="Number of EPDs Published from Tool Version"
        buttonLabel="Published EPDs"
        statsType={ToolVersionStatsType.PUBLISHED_EPDS}
        value={versionStats.publishedEpdsCount}
        dialogHeaderText="EPDs published from the tool version"
        dialogHelpText="Here you can find the list of all published EPDs with this tool version."
      />
      {toolData.toolType === ToolType.FULLY_PREVERIFIED_TOOL && toolData.hasApiIntegration && (
        <StatsItem
          variant={variant}
          toolId={toolData.id}
          versionOrd={versionData.ord}
          label="Number of updated published EPD versions from Tool Version"
          buttonLabel="Updated Published EPDs"
          statsType={ToolVersionStatsType.PUBLISHED_EPD_VERSIONS}
          value={versionStats.publishedEpdVersionsCount}
          dialogHeaderText="EPDs versions updated from the tool version"
          dialogHelpText="This list shows all updated EPDs versions from this tool version that have been successfully published."
        />
      )}
    </>
  );
};

export default ToolVersionStats;

const StatsItem: React.FC<{
  variant: 'admin' | 'toolOwner';
  toolId: string;
  versionOrd: number;
  label: string;
  buttonLabel: string;
  statsType: ToolVersionStatsType;
  value: number;
  dialogHeaderText: string;
  dialogHelpText: string;
}> = ({ variant, toolId, versionOrd, label, buttonLabel, statsType, value, dialogHeaderText, dialogHelpText }) => {
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  return (
    <div>
      {variant === 'admin' && <WizardTextInputComponent label={label} name={statsType} value={value} disabled />}
      {variant === 'toolOwner' && <OldUIReadonlyTextInput label={label} value={String(value)} />}

      <MrRobotoTextButton
        $variant={variant}
        label={buttonLabel}
        onClick={() => setIsDialogVisible(true)}
        style={{ fontSize: 14, fontWeight: 500 }}
        icon={<FontIcon icon="link" style={{ fontSize: '12px', marginRight: '5px' }} />}
        disabled={value === 0}
        text
      />

      <ShortDialog
        visible={isDialogVisible}
        onHide={() => setIsDialogVisible(false)}
        header={<HeaderDialog>{dialogHeaderText}</HeaderDialog>}
        draggable={false}
      >
        <DialogContent>
          <HelpBox>{dialogHelpText}</HelpBox>

          <ListWrapper>
            <ToolVersionStatsList
              toolId={toolId}
              versionOrd={versionOrd}
              type={statsType}
              onHide={() => setIsDialogVisible(false)}
              skeletonsCount={value}
              disableLinks={variant === 'toolOwner'}
            />
          </ListWrapper>
        </DialogContent>
      </ShortDialog>
    </div>
  );
};

const ShortDialog = styled(DialogClearStyles)`
  & .p-dialog-content {
    max-height: min(90dvw, 570px);
  }
`;

const ListWrapper = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 8px;
`;

const MrRobotoTextButton = styled(TextButton)<{ $variant: 'admin' | 'toolOwner' }>`
  ${(props) =>
    props.$variant === 'toolOwner' &&
    css`
      .p-button-label {
        font-family: Roboto !important;
      }
    `}
`;
